import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ArrowRightLeft, Pencil, Plus, Trash2 } from 'lucide-react'

import { api } from '@/api/client'
import { confirm } from '@/stores/confirm'
import type { ItemSupplier, Supplier } from '@/types/models'
import { useToast } from '@/stores/toast'
import { Modal } from '@/components/Modal'
import { ItemPicker } from '@/components/ItemPicker'
import { Field, inputCls } from '@/components/Field'
import { cn } from '@/lib/utils'

const SUP = '/api/purchase/suppliers'
const MAP = '/api/purchase/item-suppliers'

type SupForm = Omit<Supplier, 'id' | 'item_count'> & { id?: number }
const blankSup: SupForm = {
  name: '', kind: 'supplier', title: '', note: '', send_method: '', is_fruit: false, sort_order: 0, is_active: true,
}

/** 회사관리 > 구매장 > 협력업체 — 업체(구역) 관리 + 품목별 업체 지정·일괄 이동. */
export function SupplierManageTab() {
  const qc = useQueryClient()
  const push = useToast((s) => s.push)
  const [selected, setSelected] = useState<number | null>(null)
  const [search, setSearch] = useState('')
  const [supEdit, setSupEdit] = useState<SupForm | null>(null)
  const [move, setMove] = useState<{ from: number; to: string } | null>(null)
  const [add, setAdd] = useState<{ item: number | null; item_label: string } | null>(null)

  const { data: sups = [] } = useQuery({ queryKey: ['purchase-suppliers'], queryFn: () => api<Supplier[]>(SUP) })
  const { data: rows = [], isFetching } = useQuery({
    queryKey: ['purchase-item-suppliers', selected, search],
    queryFn: () => {
      const qs = new URLSearchParams()
      if (selected) qs.set('supplier', String(selected))
      if (search) qs.set('q', search)
      return api<ItemSupplier[]>(`${MAP}?${qs}`)
    },
    enabled: !!selected || !!search,
  })
  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['purchase-suppliers'] })
    qc.invalidateQueries({ queryKey: ['purchase-item-suppliers'] })
  }
  const fail = (e: Error) => push(`실패: ${e.message}`, 'error', 6000)

  const saveSup = useMutation({
    mutationFn: (f: SupForm) => f.id
      ? api(`${SUP}/${f.id}`, { method: 'PATCH', body: f })
      : api(SUP, { method: 'POST', body: f }),
    onSuccess: () => { refresh(); setSupEdit(null); push('저장됨', 'success') },
    onError: fail,
  })
  const delSup = useMutation({
    mutationFn: (id: number) => api(`${SUP}/${id}`, { method: 'DELETE' }),
    onSuccess: () => { refresh(); setSelected(null); push('삭제됨', 'success') },
    onError: fail,
  })
  const doMove = useMutation({
    mutationFn: (m: { from: number; to: string }) =>
      api<{ moved: number }>(`${SUP}/move`, { method: 'POST', body: { from: m.from, to: Number(m.to) } }),
    onSuccess: (r) => { refresh(); setMove(null); push(`${r.moved}개 품목 이동됨`, 'success') },
    onError: fail,
  })
  const setItemSup = useMutation({
    mutationFn: (v: { id: number; supplier: number }) => api(`${MAP}/${v.id}`, { method: 'PATCH', body: { supplier: v.supplier } }),
    onSuccess: () => { refresh(); push('변경됨', 'success') },
    onError: fail,
  })
  const addItem = useMutation({
    mutationFn: (v: { item: number; supplier: number }) => api(MAP, { method: 'POST', body: v }),
    onSuccess: () => { refresh(); setAdd(null); push('지정됨', 'success') },
    onError: fail,
  })
  const delItem = useMutation({
    mutationFn: (id: number) => api(`${MAP}/${id}`, { method: 'DELETE' }),
    onSuccess: () => { refresh(); push('지정 해제됨', 'success') },
    onError: fail,
  })

  const cur = sups.find((s) => s.id === selected)

  return (
    <div className="max-w-6xl mx-auto p-6 grid grid-cols-[18rem_1fr] gap-4 items-start">
      {/* 업체 목록 */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="flex items-center px-3 py-2 border-b border-slate-200 bg-slate-50">
          <span className="text-sm font-semibold text-slate-700">협력업체 · 구역</span>
          <button onClick={() => setSupEdit({ ...blankSup, sort_order: sups.length + 1 })}
                  className="ml-auto text-emerald-700 hover:text-emerald-900 text-xs font-semibold inline-flex items-center gap-0.5">
            <Plus className="w-3.5 h-3.5"/> 추가
          </button>
        </div>
        {sups.map((s) => (
          <button key={s.id} onClick={() => { setSelected(s.id); setSearch('') }}
                  className={cn('w-full text-left px-3 py-1.5 border-b border-slate-100 flex items-center gap-2 text-sm',
                    selected === s.id ? 'bg-emerald-50' : 'hover:bg-slate-50', !s.is_active && 'opacity-50')}>
            <span className="font-medium text-slate-800">{s.name}</span>
            {s.kind === 'area' && <span className="text-[10px] text-slate-500 border border-slate-300 rounded px-1">구역</span>}
            {s.is_fruit && <span className="text-[10px] text-orange-700 bg-orange-50 border border-orange-200 rounded px-1">과일</span>}
            <span className="ml-auto text-xs tabular-nums text-slate-400">{s.item_count}</span>
          </button>
        ))}
      </div>

      {/* 선택 업체 상세 + 품목 */}
      <div className="space-y-3">
        <div className="bg-white rounded-xl border border-slate-200 p-3 flex items-center gap-3 flex-wrap">
          <input value={search} onChange={(e) => setSearch(e.target.value)}
                 placeholder={cur ? `${cur.name} 안에서 품목 검색` : '품목 검색 (전 업체)'}
                 className={inputCls + ' flex-1 min-w-[200px]'}/>
          {cur && (
            <>
              <button onClick={() => setSupEdit({ ...cur })}
                      className="text-xs border border-slate-300 rounded-lg px-2.5 py-1.5 text-slate-600 hover:bg-slate-50 inline-flex items-center gap-1">
                <Pencil className="w-3.5 h-3.5"/> 업체 정보
              </button>
              <button onClick={() => setMove({ from: cur.id, to: '' })} disabled={!cur.item_count}
                      className="text-xs border border-slate-300 rounded-lg px-2.5 py-1.5 text-slate-600 hover:bg-slate-50 inline-flex items-center gap-1 disabled:opacity-40">
                <ArrowRightLeft className="w-3.5 h-3.5"/> 품목 전체 다른 업체로
              </button>
              <button onClick={() => setAdd({ item: null, item_label: '' })}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold rounded-lg px-3 py-1.5 inline-flex items-center gap-1">
                <Plus className="w-3.5 h-3.5"/> 품목 지정
              </button>
            </>
          )}
        </div>

        {cur && (cur.title || cur.note || cur.send_method) && (
          <div className="bg-white rounded-xl border border-slate-200 p-3 text-sm">
            <div className="font-semibold text-slate-800">{cur.title || cur.name}</div>
            {cur.send_method && <div className="text-xs text-slate-500 mt-0.5">전달: {cur.send_method}</div>}
            {cur.note && <div className="text-xs text-rose-600 whitespace-pre-line mt-1">{cur.note}</div>}
          </div>
        )}

        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs text-slate-500">
              <tr>
                <th className="px-3 py-2 text-left font-medium">품목</th>
                <th className="px-3 py-2 text-left font-medium w-48">협력업체</th>
                <th className="px-3 py-2 w-10"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="px-3 py-1.5"><span className="font-mono text-xs text-slate-400 mr-1">{r.item_code}</span>{r.item_name}</td>
                  <td className="px-3 py-1">
                    <select value={r.supplier} onChange={(e) => setItemSup.mutate({ id: r.id, supplier: Number(e.target.value) })}
                            className={inputCls + ' py-1 text-sm'}>
                      {sups.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                    </select>
                  </td>
                  <td className="px-3 py-1.5 text-right">
                    <button onClick={() => { void confirm(`'${r.item_name}' 업체 지정을 해제할까요?`, { danger: true }).then((ok) => ok && delItem.mutate(r.id)) }}
                            className="text-rose-500 hover:text-rose-700"><Trash2 className="w-3.5 h-3.5"/></button>
                  </td>
                </tr>
              ))}
              {!rows.length && !isFetching && (
                <tr><td colSpan={3} className="text-center py-10 text-sm text-slate-400">
                  {selected || search ? '지정된 품목이 없습니다.' : '왼쪽에서 업체를 고르거나 품목을 검색하세요.'}
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <Modal open={!!supEdit} onClose={() => setSupEdit(null)} title={supEdit?.id ? '업체 정보 수정' : '업체 추가'} size="lg">
        {supEdit && (
          <form onSubmit={(e) => { e.preventDefault(); if (supEdit.name.trim()) saveSup.mutate(supEdit) }} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <Field label="업체명 *">
                <input value={supEdit.name} onChange={(e) => setSupEdit({ ...supEdit, name: e.target.value })} className={inputCls}/>
              </Field>
              <Field label="구분">
                <select value={supEdit.kind} onChange={(e) => setSupEdit({ ...supEdit, kind: e.target.value as SupForm['kind'] })} className={inputCls}>
                  <option value="supplier">협력업체 (발주서 대상)</option>
                  <option value="area">구역 (A·B·C·D, 발주서 없음)</option>
                </select>
              </Field>
            </div>
            <Field label="발주서 머리글 (업체명·팩스)" full>
              <input value={supEdit.title} onChange={(e) => setSupEdit({ ...supEdit, title: e.target.value })}
                     placeholder="예: 보송농산 (Fax:448-7354)" className={inputCls}/>
            </Field>
            <Field label="요청문 (줄마다 한 문장, 발주서 머리글 아래 빨간 글씨)" full>
              <textarea value={supEdit.note} onChange={(e) => setSupEdit({ ...supEdit, note: e.target.value })}
                        rows={5} className={inputCls + ' font-normal'}/>
            </Field>
            <div className="grid grid-cols-3 gap-3">
              <Field label="전달 방법">
                <input value={supEdit.send_method} onChange={(e) => setSupEdit({ ...supEdit, send_method: e.target.value })}
                       placeholder="엑셀 카톡 / 팩스 / 출력만" className={inputCls}/>
              </Field>
              <Field label="순서">
                <input type="number" value={supEdit.sort_order} onChange={(e) => setSupEdit({ ...supEdit, sort_order: Number(e.target.value) || 0 })}
                       className={inputCls}/>
              </Field>
              <div className="flex flex-col justify-end gap-1 text-sm pb-1">
                <label className="inline-flex items-center gap-1.5">
                  <input type="checkbox" checked={supEdit.is_fruit} onChange={(e) => setSupEdit({ ...supEdit, is_fruit: e.target.checked })}/> 과일 구매장
                </label>
                <label className="inline-flex items-center gap-1.5">
                  <input type="checkbox" checked={supEdit.is_active} onChange={(e) => setSupEdit({ ...supEdit, is_active: e.target.checked })}/> 사용
                </label>
              </div>
            </div>
            <div className="flex justify-between pt-2">
              {supEdit.id ? (
                <button type="button" onClick={() => { void confirm(`'${supEdit.name}' 업체를 삭제할까요? (지정 품목이 있으면 삭제되지 않습니다)`, { danger: true }).then((ok) => { if (ok) { delSup.mutate(supEdit.id!); setSupEdit(null) } }) }}
                        className="px-3 py-2 text-sm text-rose-600 hover:bg-rose-50 rounded-lg">삭제</button>
              ) : <span/>}
              <div className="flex gap-2">
                <button type="button" onClick={() => setSupEdit(null)} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg">취소</button>
                <button type="submit" disabled={!supEdit.name.trim() || saveSup.isPending}
                        className="px-4 py-2 text-sm font-semibold rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-40">저장</button>
              </div>
            </div>
          </form>
        )}
      </Modal>

      <Modal open={!!move} onClose={() => setMove(null)} title="품목 전체 다른 업체로 옮기기">
        {move && (
          <form onSubmit={(e) => { e.preventDefault(); if (move.to) doMove.mutate(move) }} className="space-y-3">
            <p className="text-sm text-slate-600">
              <b>{sups.find((s) => s.id === move.from)?.name}</b>에 지정된 품목
              {' '}{sups.find((s) => s.id === move.from)?.item_count}개를 모두 아래 업체로 옮깁니다.
              새 업체라면 먼저 "추가"로 만들어 주세요.
            </p>
            <select value={move.to} onChange={(e) => setMove({ ...move, to: e.target.value })} className={inputCls}>
              <option value="">옮길 업체 선택</option>
              {sups.filter((s) => s.id !== move.from).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setMove(null)} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg">취소</button>
              <button type="submit" disabled={!move.to || doMove.isPending}
                      className="px-4 py-2 text-sm font-semibold rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-40">옮기기</button>
            </div>
          </form>
        )}
      </Modal>

      <Modal open={!!add} onClose={() => setAdd(null)} title={`${cur?.name ?? ''}에 품목 지정`}>
        {add && cur && (
          <form onSubmit={(e) => { e.preventDefault(); if (add.item) addItem.mutate({ item: add.item, supplier: cur.id }) }} className="space-y-3">
            <Field label="품목 *" full>
              <ItemPicker value={add.item} initialLabel={add.item_label}
                          onChange={(it) => setAdd({ item: it.id, item_label: `${it.code} ${it.name}` })}/>
            </Field>
            <p className="text-xs text-slate-500">이미 다른 업체에 지정된 품목은 목록에서 업체를 바꿔 주세요.</p>
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setAdd(null)} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg">취소</button>
              <button type="submit" disabled={!add.item || addItem.isPending}
                      className="px-4 py-2 text-sm font-semibold rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-40">지정</button>
            </div>
          </form>
        )}
      </Modal>
    </div>
  )
}
